import { useEffect, useState } from "react";

// Phase 21: persistent geo selector. Choices: National (default) | State | City.
// Stored in localStorage so the selection survives reloads. Compact UI
// suitable for the Overview header and the Command Center top bar.

export interface GeoSelection {
  scope: "national" | "state" | "city";
  state?: string;
  city?: string;
}

const STORAGE_KEY = "bios:geo:selection";

const STATE_OPTIONS: Array<{ code: string; label: string }> = [
  { code: "AL", label: "Alabama" }, { code: "AK", label: "Alaska" },
  { code: "AZ", label: "Arizona" }, { code: "AR", label: "Arkansas" },
  { code: "CA", label: "California" }, { code: "CO", label: "Colorado" },
  { code: "CT", label: "Connecticut" }, { code: "DC", label: "District of Columbia" },
  { code: "DE", label: "Delaware" }, { code: "FL", label: "Florida" },
  { code: "GA", label: "Georgia" }, { code: "HI", label: "Hawaii" },
  { code: "ID", label: "Idaho" }, { code: "IL", label: "Illinois" },
  { code: "IN", label: "Indiana" }, { code: "IA", label: "Iowa" },
  { code: "KS", label: "Kansas" }, { code: "KY", label: "Kentucky" },
  { code: "LA", label: "Louisiana" }, { code: "ME", label: "Maine" },
  { code: "MD", label: "Maryland" }, { code: "MA", label: "Massachusetts" },
  { code: "MI", label: "Michigan" }, { code: "MN", label: "Minnesota" },
  { code: "MS", label: "Mississippi" }, { code: "MO", label: "Missouri" },
  { code: "MT", label: "Montana" }, { code: "NE", label: "Nebraska" },
  { code: "NV", label: "Nevada" }, { code: "NH", label: "New Hampshire" },
  { code: "NJ", label: "New Jersey" }, { code: "NM", label: "New Mexico" },
  { code: "NY", label: "New York" }, { code: "NC", label: "North Carolina" },
  { code: "ND", label: "North Dakota" }, { code: "OH", label: "Ohio" },
  { code: "OK", label: "Oklahoma" }, { code: "OR", label: "Oregon" },
  { code: "PA", label: "Pennsylvania" }, { code: "RI", label: "Rhode Island" },
  { code: "SC", label: "South Carolina" }, { code: "SD", label: "South Dakota" },
  { code: "TN", label: "Tennessee" }, { code: "TX", label: "Texas" },
  { code: "UT", label: "Utah" }, { code: "VT", label: "Vermont" },
  { code: "VA", label: "Virginia" }, { code: "WA", label: "Washington" },
  { code: "WV", label: "West Virginia" }, { code: "WI", label: "Wisconsin" },
  { code: "WY", label: "Wyoming" },
];

export function loadGeoSelection(): GeoSelection {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { scope: "national" };
    const parsed = JSON.parse(raw) as GeoSelection;
    if (parsed && (parsed.scope === "national" || parsed.scope === "state" || parsed.scope === "city")) {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return { scope: "national" };
}

export function saveGeoSelection(sel: GeoSelection) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sel));
  } catch {
    /* ignore */
  }
}

export function GeoSelector({
  value,
  onChange,
}: {
  value: GeoSelection;
  onChange: (next: GeoSelection) => void;
}) {
  const [draftCity, setDraftCity] = useState(value.city ?? "");

  useEffect(() => {
    setDraftCity(value.city ?? "");
  }, [value.city]);

  function commit(next: GeoSelection) {
    saveGeoSelection(next);
    onChange(next);
  }

  return (
    <div className="bios-geo-selector" role="group" aria-label="Geographic scope">
      <button
        type="button"
        className={`bios-geo-tab${value.scope === "national" ? " active" : ""}`}
        onClick={() => commit({ scope: "national" })}
      >
        National
      </button>
      <div className={`bios-geo-tab${value.scope === "state" ? " active" : ""}`}>
        <span style={{ marginRight: 6 }}>State</span>
        <select
          value={value.scope === "state" ? value.state ?? "" : ""}
          onChange={(e) => {
            const code = e.target.value;
            if (!code) {
              commit({ scope: "national" });
            } else {
              commit({ scope: "state", state: code });
            }
          }}
          aria-label="Select state"
        >
          <option value="">—</option>
          {STATE_OPTIONS.map((s) => (
            <option key={s.code} value={s.code}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <div className={`bios-geo-tab${value.scope === "city" ? " active" : ""}`}>
        <span style={{ marginRight: 6 }}>City</span>
        <input
          type="text"
          placeholder="e.g. Chicago"
          value={draftCity}
          onChange={(e) => setDraftCity(e.target.value)}
          onBlur={() => {
            const trimmed = draftCity.trim();
            if (!trimmed) {
              if (value.scope === "city") commit({ scope: "national" });
            } else {
              commit({ scope: "city", city: trimmed, state: value.state });
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          aria-label="City filter"
          style={{ width: 110 }}
        />
      </div>
    </div>
  );
}
